import React, { useState, useEffect } from 'react';
import WidgetSectionTitle from 'widgets/WidgetSectionTitle';
import { Col, Container, Row, Spinner } from 'react-bootstrap';
import EChartsGeoJSONMap from './GeoMap';
import SectionComponent from './sectionComponent';
import { sessionObjects } from '../session/sessionData';
import axios from 'axios';

const domain = window.location.hostname;
let xHost = '';
if (domain.includes('.uat')) {
  xHost = 'fcs-api.uat.farmsio.com';
} else {
  xHost = 'fcs-beam-api-v2.farmsio.com';
}

function Dashboard() {
  // Main state for the district selected by the map
  const [selectedMapDistrictId, setSelectedMapDistrictId] = useState(null);
  const [seasonData, setSeasonData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // State to track the currently selected district in each section
  const [sectionDistrictIds, setSectionDistrictIds] = useState({
    farmers: null,
    procurement: null,
    milling: null,
    infrastructure: null
  });

  // Handle district click from the map
  const handleDistrictClick = districtId => {
    // console.log('District selected in parent component:', districtId);

    // Update the map selection
    setSelectedMapDistrictId(districtId);

    // Update all section selections to match
    setSectionDistrictIds({
      farmers: districtId,
      procurement: districtId,
      milling: districtId,
      infrastructure: districtId
    });
  };

  // Handle when a section's district selection changes
  const handleSectionDistrictChange = (sectionKey, districtId) => {
    // Update just this section's district ID
    setSectionDistrictIds(prev => ({
      ...prev,
      [sectionKey]: districtId
    }));
  };

  // Determine what should be shown on the map
  // If all sections have the same district selected, use that
  // Otherwise, clear the map selection
  const [displayedMapDistrictId, setDisplayedMapDistrictId] = useState(null);

  useEffect(() => {
    // Get all unique district IDs (excluding null)
    const uniqueDistrictIds = new Set(
      Object.values(sectionDistrictIds).filter(id => id !== null)
    );

    // If there's exactly one unique ID and all sections have it selected,
    // then display that on the map
    if (
      uniqueDistrictIds.size === 1 &&
      Object.values(sectionDistrictIds).every(
        id => id === Array.from(uniqueDistrictIds)[0] || id === null
      )
    ) {
      setDisplayedMapDistrictId(Array.from(uniqueDistrictIds)[0]);
    } else if (uniqueDistrictIds.size > 1) {
      // If there are different district IDs selected, clear the map selection
      setDisplayedMapDistrictId(null);
    }
  }, [sectionDistrictIds]);

  // Headers for API calls
  const headers = {
    'x-client-host': xHost,
    'Content-Type': 'application/json',
    Authorization: `Bearer ${sessionObjects.access_token}`
  };

  // API base URL
  const apiBaseUrl = `${process.env.REACT_APP_FCS_API_URL}`;

  // Fetch season data from the additional API
  const fetchSeasonData = async () => {
    try {
      setLoading(true);
      console.log('Parent: Fetching season data...');

      const url = `${apiBaseUrl}api/documents/get-list-record-sets`;
      const requestBody = {
        userId: 'bd30c685-6446-4c9b-9336-a96c66550072',
        key: 'season_data_recordset_api',
        id: '',
        param1: '',
        param2: '',
        param3: '',
        param4: '',
        param5: '',
        param6: '',
        param7: '',
        param8: '',
        param9: '',
        param10: '',
        limit: '100',
        offset: '0',
        sessionObjects: sessionObjects
      };

      const response = await axios.post(url, requestBody, { headers });

      if (response.data && response.data.Status === 'SUCCESS') {
        const seasonRecordsView = response.data.Result.recordsView?.[0];
        if (seasonRecordsView && seasonRecordsView.datas) {
          console.log('Parent: Season data loaded successfully');
          setSeasonData(seasonRecordsView.datas);
        } else {
          console.error('Parent: No season data available in the response');
          setError('Failed to load season data');
        }
      } else {
        console.error('Parent: Failed to fetch season data');
        setError('Failed to fetch season data');
      }
    } catch (err) {
      console.error('Parent: API Error when fetching season data:', err);
      setError(`Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Fetch season data on component mount
  useEffect(() => {
    fetchSeasonData();
  }, []);

  // Show loading spinner while fetching season data
  if (loading && !seasonData.length) {
    return (
      <Container className="text-center my-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading season data...</span>
        </Spinner>
        <p className="mt-2">Loading season data...</p>
      </Container>
    );
  }
  return (
    <Container>
      <Row className="mb-3">
        <Col lg={12} xxl={12}>
          <EChartsGeoJSONMap
            onDistrictClick={handleDistrictClick}
            selectedDistrictId={displayedMapDistrictId}
          />
        </Col>
      </Row>

      <WidgetSectionTitle
        title="Farmers Records"
        transform="shrink-4"
        className="mb-4 mt-4 align-items-center"
        color="#3C0008"
      />
      <SectionComponent
        keyId={'farmer_records_dashboard_fcs'}
        stateId={sectionDistrictIds.farmers}
        onDistrictChange={districtId =>
          handleSectionDistrictChange('farmers', districtId)
        }
        seasonData={seasonData}
      />

      <WidgetSectionTitle
        title="State Procurement and Supply"
        transform="shrink-4"
        className="mb-4 mt-6 align-items-center"
        color="#E86100"
      />
      <SectionComponent
        keyId={'state_procurement_supply_dashboard_fcs'}
        stateId={sectionDistrictIds.procurement}
        onDistrictChange={districtId =>
          handleSectionDistrictChange('procurement', districtId)
        }
        seasonData={seasonData}
      />

      <WidgetSectionTitle
        title="Milling Supply and Dispatch"
        transform="shrink-4"
        className="mb-4 mt-4 align-items-center"
      />
      <SectionComponent
        keyId={'milling_supply_dispatch_dashboard_fcs'}
        stateId={sectionDistrictIds.milling}
        onDistrictChange={districtId =>
          handleSectionDistrictChange('milling', districtId)
        }
        seasonData={seasonData}
      />

      <WidgetSectionTitle
        title="Infrastructure"
        transform="shrink-4"
        className="mb-4 mt-4 align-items-center"
        color="#1E2A56"
      />
      <SectionComponent
        keyId={'infrastructure_records_dashboard_fcs'}
        stateId={sectionDistrictIds.infrastructure}
        onDistrictChange={districtId =>
          handleSectionDistrictChange('infrastructure', districtId)
        }
        seasonData={seasonData}
      />
    </Container>
  );
}

export default Dashboard;
